import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TextareaAutosize, TextField, Grid, FormControlLabel, Checkbox, Typography, FormHelperText } from '@mui/material';
import { useAPIAuthenticatedService } from '../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralCreationAttributes_FromUser } from '../helpers/DataStructures';
import AsyncActionButton, { AsyncActionButtonAbortError } from '../components/AsyncActionButton';


interface CreateReferralFormProps {
  offerId: number; // Assuming the offer id is provided as a prop
  closeDialog: () => void;
}

const CreateReferralForm: React.FC<CreateReferralFormProps> = ({ offerId, closeDialog }) => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const lifecyclePrefix = 'lifecycle-debug';

  useEffect(() => {
    console.log(`[${lifecyclePrefix}] CreateReferralForm mount`, { offerId });

    return () => {
      console.log(`[${lifecyclePrefix}] CreateReferralForm unmount`, { offerId });
    };
  }, []);

  const { handleSubmit, control, formState: { errors }} = useForm<ReferralCreationAttributes_FromUser>({
    defaultValues: {
      theReferralName: '',
      theReferralContact: '',
      theReferralDetails: '',
      customerConsent: false,
    },
  });

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationKey: [CacheKeys.REFERRALS_FROM_USER],
    mutationFn: apiServiceInstance.createReferral,
    onSuccess: () => {
      void queryClient.invalidateQueries([CacheKeys.REFERRALS_FROM_USER]);
    },
  });

  const submitReferral = handleSubmit(
    async (data: ReferralCreationAttributes_FromUser) => {
      // REQ-051: do not log referral form payloads or validation objects; they can contain customer data.
      await mutateAsync({ ...data, offerId });
    },
    async () => {
      throw new AsyncActionButtonAbortError();
    }
  );

  return (
    <form>
      <Grid container direction="column" spacing={2}>       

        <Grid item xs={12}>
            <Controller
                name="theReferralName"
                control={control}
                rules={{ required: 'Referral name is required' }}
                render={({ field }) => <TextField {...field} label="Referral Name" error={Boolean(errors.theReferralName)} 
                helperText={errors.theReferralName?.message} fullWidth />}
            />
        </Grid>

        <Grid item xs={12}>
            <Controller
                name="theReferralContact"
                control={control}
                rules={{ required: 'Referral contact is required' }}
                render={({ field }) => <TextField {...field} label="Referral Contact" error={Boolean(errors.theReferralContact)} 
                helperText={errors.theReferralContact?.message} fullWidth />}
            />
        </Grid>

        <Grid item xs={12}>
                <Controller
                  name="theReferralDetails"
                  control={control}
                  render={({ field }) =>
                    <TextareaAutosize
                      {...field}
                      minRows={4}
                      placeholder="Referral Details"
                      style={{ width: "100%", fontFamily: "inherit", fontSize: "1rem" }}
                    />
                  }
                />
        </Grid>

        <Grid item xs={12}>
        <Controller 
            name="customerConsent"
            control={control}
            rules={{ required: 'You must get permission from the customer to share their contact details' }}
            defaultValue={false}
            render={({ field }) => 
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={
                  <Typography variant="body2" sx={{ textAlign:"start"}}>
                    I have asked the customer and they have consented to share their contact details in order to make the referral
                  </Typography>
                }
                labelPlacement="end"
              />
            }
            />
            {errors.customerConsent && <FormHelperText error>{errors.customerConsent.message}</FormHelperText>}
        </Grid>

        <Grid item xs={12}>
            <AsyncActionButton
              errorToast="Error"
              fullWidth
              idleLabel="Create Referral"
              onAction={async () => {
                await submitReferral();
              }}
              onAfterSuccessDisplay={async () => {
                await closeDialog();
              }}
              pendingLabel="Sending..."
              styleVariant="green"
              successDisplayMs={250}
              successLabel="Referral Sent"
              successToast="Referral Sent"
            />
        </Grid>
      </Grid>
    </form>
  );
};

export default CreateReferralForm;
